import { readFileSync } from 'fs';
import { groupBy, includes, isEmpty } from 'lodash-es';
import * as React from 'react';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import path from 'path';
import Head from 'next/head';
import ConversionWidgetComponent, { ConversionWidgetEvent } from '../components/conversion-widget/convertions-widget.component';
import { Measure, UnitConversion } from '../types/types';
import convert from '../utils/convert-utils';
import SearchBox from '../components/search-box/search-box.component';
import { AdBox, AdSlots } from '../components/ad-box.component';
import styles from './home.component.module.scss';

import { NextRouter, withRouter } from 'next/router';
import { unitConversionFormatter } from '../utils/unit-formatter';
import { rootLinkBlocks } from '../utils/link-hierarchy.server';

export async function getStaticProps({ params }) {
  // Read the freshly-synced ssr-data (same source as [id].tsx). The old
  // ../unit-converter-ssr/build/route-content path was stale build output not covered by
  // `copy:ssr-data`, so the home page was rendering from pre-batch data.
  const routeContentDir = path.resolve(process.cwd(), `ssr-data`, `build`, `route-content`);
  const indexDataJson = path.join(routeContentDir, `index.json`);
  const indexData = readFileSync(indexDataJson, 'utf8')
  return {
    props: {
      ...JSON.parse(indexData),
      // Link hierarchy (METHOD §4): File tools row + More converters slot on the app root (rendered by _app).
      linkBlocks: rootLinkBlocks(),
    }
  }
}

interface HomePageState {
  measureFilter: string; // measure
  initialMeasure: Measure,
  unitConversions: UnitConversion[];
  filteredUnitConversionsByMeasure: { [key: string]: UnitConversion[] };
  keywordFilter: (v) => boolean;
}

interface HomePageProps {
  router: NextRouter
}

class HomeComponent extends React.Component<HomePageProps, HomePageState> {
  measureToData: {
    [unit: string]: {
      measureReadableName: string
      measureUnits: {
        urlText: string | React.JSX.Element;
        searchableText: string;
        url: string;
      }[]
    }
  } = {};
  nameCache = {};
  sortedMeasureToDataKeys = [];
  filterRef = React.createRef<HTMLSelectElement>();
  keywordFilterRef = React.createRef<HTMLInputElement>();

  searchSubject = new Subject();

  private measures: Measure[] = [];

  constructor(props: HomePageProps) {
    super(props);

    const conversions: UnitConversion[] = props['unitConversions'];
    const unitConversionsByMeasure: { [key: string]: UnitConversion[] } = groupBy(conversions, 'measure') as any

    for (const converter in unitConversionsByMeasure) {
      const unitConversions = unitConversionsByMeasure[converter];
      this.measures.push({
        measure: unitConversions[0].measure,
        measureName: unitConversions[0].measureName
      });
      this.measureToData[unitConversions[0].measure] = {
        measureReadableName: unitConversions[0].measureName,
        measureUnits: unitConversions
          .filter(a => a.toUnit != a.fromUnit)
          .map(unitConversion => {
            const searchableText = `${unitConversion.fromUnitName} ${unitConversion.fromUnit} to ${unitConversion.toUnitName} ${unitConversion.toUnit}`;
            return {
              urlText: unitConversionFormatter({
                readableFromAbbr: unitConversion.fromUnit,
                readableFromPlural: unitConversion.fromUnitName,
                readableToAbbr: unitConversion.toUnit,
                readableToPlural: unitConversion.toUnitName
              }),
              searchableText: searchableText,
              url: `/unit-converter/${UnitConversion.linkUrlPart(unitConversion)}`,
            }
          }),
      };
    }

    const { measure } = this.getQueryParams() || {};
    var initialMeasure = this.measures.find((a) => a.measure == measure);
    if (!initialMeasure) {
      initialMeasure = this.measures[1];
    }

    // console.log(initialMeasure);
    this.state = {
      initialMeasure: initialMeasure,
      measureFilter: null,
      unitConversions: conversions,
      filteredUnitConversionsByMeasure: unitConversionsByMeasure,
      keywordFilter: (a) => true
    };

    this.updateSortedMeasureToDataKeys();
    this.searchSubject.pipe(debounceTime(100)).subscribe({
      next: (filter) => {
        this.setState(filter);
      }
    })
  }

  getUnitDescription(unit) {
    if (!this.nameCache[unit]) {
      this.nameCache[unit] = convert().describe(unit).plural;
    }
    return this.nameCache[unit]
  }

  updateSortedMeasureToDataKeys() {
    this.sortedMeasureToDataKeys = Object.keys(this.measureToData).sort((a, b) => this.measureToData[a].measureUnits.length - this.measureToData[b].measureUnits.length)
  }

  updateFilter() {
    this.setState({
      measureFilter: this.filterRef.current.value === 'all' ? null : this.filterRef.current.value
    });
  }

  keywordFilterChanged() {
    const filterTokens = (this.keywordFilterRef.current.value || '').split(/\W/)
      .map(token => token.trim())
      .filter(token => !isEmpty(token))
      .filter(token => token !== 'to' && token !== 'and' && token.length > 1);
    if (filterTokens.length === 0) {
      this.searchSubject.next({
        keywordFilter: (a) => true,
      });
      return;
    }
    this.searchSubject.next({
      keywordFilter: (searchText) => {
        return filterTokens.every(t => searchText.match(new RegExp(t, "i")));
      },
    });
  }

  handleConversionWidgetEvent(event: ConversionWidgetEvent) {
    // console.log(event);
    const unitConversion = this.state.unitConversions.find((a) => a.fromUnit == event.fromUnit && a.toUnit == event.toUnit);
    if (unitConversion) {
      const url = UnitConversion.linkUrlPart(unitConversion);
      console.log(url);
      this.setState({
        initialMeasure: this.measures.find(a => a.measure == event.measure),
      });
      var unitConversionURL: string = null;
      if (includes(event.changedProperties, 'measure')) {
        // both to and from measures need to be updated to first and second unit for this measure
        const unitConversionForMeasure = this.state.unitConversions
          .find((a) => a.measure == event.measure && a.fromUnit != a.toUnit);
        unitConversionURL = UnitConversion.linkUrlPart(unitConversionForMeasure);
      } else if (includes(event.changedProperties, 'fromUnit') && includes(event.changedProperties, 'toUnit')) {
        const unitConversionForMeasure = this.state.unitConversions
          .find((a) => a.fromUnit == event.fromUnit && a.toUnit == event.toUnit);
        unitConversionURL = UnitConversion.linkUrlPart(unitConversionForMeasure);
      } else if (includes(event.changedProperties, 'fromUnit')) {
        const unitConversionForMeasure = this.state.unitConversions
          .find((a) => a.fromUnit == event.fromUnit && a.toUnit == event.toUnit);
        if (unitConversionForMeasure.fromUnit == unitConversionForMeasure.toUnit) {
          return;
        }
        unitConversionURL = UnitConversion.linkUrlPart(unitConversionForMeasure);
      } else if (includes(event.changedProperties, 'toUnit')) {
        const unitConversionForMeasure = this.state.unitConversions
          .find((a) => a.fromUnit == event.fromUnit && a.toUnit == event.toUnit);
        if (unitConversionForMeasure.fromUnit == unitConversionForMeasure.toUnit) {
          return;
        }
        unitConversionURL = UnitConversion.linkUrlPart(unitConversionForMeasure);
      } else if (includes(event.changedProperties, 'fromValue')) {
        const unitConversionForMeasure = this.state.unitConversions
          .find((a) => a.fromUnit == event.fromUnit && a.toUnit == event.toUnit);
        unitConversionURL = UnitConversion.linkUrlPart(unitConversionForMeasure);
      }
      if (unitConversionURL) {
        if (includes(event.changedProperties, 'fromValue')) {
          window.history.replaceState({ value: event.fromValue, measure: event.measure }, '', `/unit-converter/${unitConversionURL}/?measure=${event.measure}&value=${event.fromValue}`);
        } else {
          this.props.router.push(`/${unitConversionURL}/?measure=${event.measure}&value=${event.fromValue}`);
        }
      }
    }
  }

  private getQueryParams() {
    const searchParams = new URLSearchParams(this.props.router.asPath.substring(this.props.router.asPath.indexOf("?")));

    const measure = searchParams.get('measure') || undefined;
    const value = searchParams.get('value');

    return {
      measure,
      value,
    };
  }

  render() {
    const filteredCategories = this.sortedMeasureToDataKeys
      .filter((a) => !this.state.measureFilter ? true : a === this.state.measureFilter)
      .filter(measure => {
        return this.measureToData[measure].measureUnits.filter(unitDesc => {
          return this.state.keywordFilter(unitDesc.searchableText)
        }).length > 0;
      });

    return <>
      <Head>
        <link rel="canonical" href="https://www.xconvert.com/unit-converter" />
      </Head>
      {/* Hero Section */}
      <div className={styles['hero-section']}>
        <h1>Unit Converter</h1>
        <p className={styles.subtitle}>
          Convert between different units of measurement quickly and easily
        </p>
        <SearchBox initialQuery={typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('q') || '' : ''} />
      </div>

      {/* Ad: below the H1, above the widget (approved 2026-09-07) */}
      <AdBox slot={AdSlots.pageTop} variant="banner" />

      {/* Conversion Widget */}
      <div className={styles['widget-wrapper']}>
        <ConversionWidgetComponent
          unitConversions={this.state.unitConversions}
          measures={this.measures}
          initialMeasure={this.state.initialMeasure}
          onChange={(e) => this.handleConversionWidgetEvent(e)}
        ></ConversionWidgetComponent>
      </div>

      {/* Ad: after the widget (approved 2026-09-07) */}
      <AdBox slot={AdSlots.belowTool} variant="rect" />

      {/* Filter Section */}
      <div className={styles['filter-section']}>
        <h2>Browse All Conversions</h2>
        <div className={styles['filters-container']}>
          <select
            className={styles.filter}
            ref={this.filterRef}
            onChange={() => this.updateFilter()}
            aria-label="Filter by category"
          >
            <option value="all">All Categories</option>
            {
              this.sortedMeasureToDataKeys.map(measure => {
                return <option key={this.measureToData[measure].measureReadableName} value={measure}>
                  {this.measureToData[measure].measureReadableName}
                </option>
              })
            }
          </select>

          <input
            className={styles.filter}
            ref={this.keywordFilterRef}
            onInput={() => this.keywordFilterChanged()}
            placeholder="Search conversions..."
            aria-label="Search conversions"
          />
        </div>
      </div>

      {/* Categories Grid */}
      {filteredCategories.length > 0 ? (
        <div className={styles['categories-grid']}>
          {filteredCategories.map(measure => {
            const filteredUnits = this.measureToData[measure].measureUnits.filter(unitDesc => {
              return this.state.keywordFilter(unitDesc.searchableText)
            });

            return (
              <div className={styles['category-card']} key={this.measureToData[measure].measureReadableName}>
                {/* The heading links its measure hub. Without this the index links all ~15k
                    conversion pages but never a single hub, leaving the hubs reachable only
                    via breadcrumbs despite being the topical landing page for the measure. */}
                <h2>
                  <a href={`/unit-converter/${measure}`}>{this.measureToData[measure].measureReadableName}</a>
                </h2>
                <ul>
                  {filteredUnits.map((unitDesc, i) => {
                    return <li key={`${unitDesc.url}${i}`}>
                      <a href={`${unitDesc.url}`}>
                        {unitDesc.urlText}
                      </a>
                    </li>
                  })}
                </ul>
              </div>
            )
          })}
        </div>
      ) : (
        <div className={styles['empty-state']}>
          <p>No conversions found matching your search.</p>
        </div>
      )}
    </>
  }
}

export default withRouter(HomeComponent)